import React, { useCallback, useEffect, useMemo, type MouseEvent, type KeyboardEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants';
import { BusinessDocumentTitle } from '@atlassian/jira-business-entity-common';
import { useQueryBasedViewAutomaticTracking } from '@atlassian/jira-business-experience-tracking';
import { isJwmInlineFieldCreateEnabled } from '@atlassian/jira-business-feature-flags';
import {
	useSorting,
	useFilteredViewContextInput,
	useSimpleSearchJQL,
} from '@atlassian/jira-business-filters';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create';
import { ModalIssueApp, SidebarIssueView } from '@atlassian/jira-business-issue-view';
import { PrefetchIssueDataController } from '@atlassian/jira-business-issue-view-critical-data-loader';
import { useIssueModalActions } from '@atlassian/jira-business-issue-view/src/common/use-issue-modal-actions';
import { isModifiedEvent } from '@atlassian/jira-business-modified-click-event';
import { ListEmbedOnboarding } from '@atlassian/jira-business-onboarding-list-embed';
import { RenderStartMark } from '@atlassian/jira-business-performance';
import { BUSINESS_LIST } from '@atlassian/jira-common-constants/src/analytics-sources';
import { DevDetailsDialogLoaderAsync } from '@atlassian/jira-development-details-loader';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useViewMode, VIEW_MODE_SIDEBAR } from '@atlassian/jira-issue-context-service';
import {
	useAnalyticsEvents,
	AnalyticsEventToProps,
	fireUIAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useQueryParam } from '@atlassian/jira-router';
import { toIssueKey } from '@atlassian/jira-shared-types';
import UFOSegment from '@atlassian/jira-ufo-segment';
import UFOLoadHold from '@atlassian/ufo-load-hold';
import usePressTracing from '@atlassian/ufo-use-press-tracing';
import { VIEW_EXPERIENCE } from '../../common/constants';
import { useInteractionMetrics, COLUMN_RESIZED } from '../../common/utils/interaction-metrics';
import { useWatchBulkSelection } from '../../controllers/bulk-selection';
import { BulkSelectionContainer } from '../../controllers/bulk-selection/bulk-selection-store';
import { useColumnWidths } from '../../controllers/column-widths';
import { useJWMListFeatures } from '../../controllers/features-context';
import { UpdateFieldsDetail } from '../../controllers/get-issue-fields';
import { useGroupBy } from '../../controllers/group-by';
import GroupUpdater from '../../controllers/group-updater';
import { useHierarchyStore, Hierarchy } from '../../controllers/hierarchy';
import { useHierarchyConfig } from '../../controllers/hierarchy/hierarchy-config';
import { useIssueChangeUpdater } from '../../controllers/issue-change-updater';
import ListActionsBanner from '../../controllers/list-actions-banner';
import ListViewUpdater from '../../controllers/list-updater';
import { useShouldShowReparentModal } from '../../controllers/reparent-modal';
import { useTableAnalytics } from '../../controllers/table-analytics';
import { useClearStaleGroupBy } from '../../controllers/use-clear-stale-group-by';
import { useIssueUpdated } from '../../controllers/use-issue-updated';
import type { ListViewProps } from '../../types';
import BaseTable, { useHighlightDoneItems } from './base-table';
import BulkDeleteModal from './bulk-delete-modal';
import BulkEdit from './bulk-edit';
import { EmptyState } from './empty-state';
import { GroupItem } from './group-item';
import HeaderActions from './header-actions';
import messages from './messages';
import { InteractionMetrics } from './metrics';
import { AsyncReparentModal } from './reparent-modal';
import { useGetColumns } from './utils';

const FOOTER_HEIGHT = 45;
const ROW_HEIGHT = 40;
const ON_END_REACHED_THRESHOLD = 300;

// passing in empty componentName so that we can define action name from analytics events
const BaseTableWithAnalytics = AnalyticsEventToProps('', {
	onResize: 'resized',
})((props) => <BaseTable {...props} />);

const ListContent = ({ projectId, projectKey, projectName }: ListViewProps) => {
	const { formatMessage } = useIntl();
	const interactionMetrics = useInteractionMetrics();
	const [columnWidths, { setColumnWidth }] = useColumnWidths();
	const [
		{
			issuesMap,
			hierarchyData: { loading, error },
		},
	] = useHierarchyStore();
	const viewMode = useViewMode();
	const isViewModeSideBar = viewMode === VIEW_MODE_SIDEBAR;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isFiltering } = useFilteredViewContextInput({ view: 'list' });
	const { isSearching } = useSimpleSearchJQL();
	// Remove after clean up of jsw_list_view_-_all_the_fields
	const [selectedIssueKey, setSelectedIssueKey] = useQueryParam(SELECTED_ISSUE_PARAM);
	const showReparentModal = useShouldShowReparentModal();

	useQueryBasedViewAutomaticTracking(VIEW_EXPERIENCE, {
		loading,
		error,
	});

	const [{ sortBy }] = useSorting();
	const isSorting = sortBy != null;

	const { isOnboardingEnabled, featureView, isSoftwareFieldsEnabled } = useJWMListFeatures();

	useTableAnalytics();

	const ufoPressTracing = usePressTracing('jwm.list.issue-view-opened');

	// Remove after clean up of jsw_list_view_-_all_the_fields
	let openIssueModal =
		(issue: { id: string; key: string }) => (event: MouseEvent | KeyboardEvent) => {
			if (issue.id && !isOptimisticIssue(Number(issue.id)) && issue.key) {
				if (!isModifiedEvent(event)) {
					ufoPressTracing(event.timeStamp);
					setSelectedIssueKey(issue.key);
					event.preventDefault();
					event.stopPropagation();
				}
			}
		};

	if (ff('jwm.list.rendering-improvements')) {
		// Replace with inline function when cleaning up 'jwm.list.rendering-improvements'
		// eslint-disable-next-line react-hooks/rules-of-hooks
		openIssueModal = useCallback(openIssueModal, [setSelectedIssueKey, ufoPressTracing]);
	}

	if (fg('jsw_list_view_-_all_the_fields')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const onBeforeOpened = useCallback(
			(event: MouseEvent | KeyboardEvent) => {
				ufoPressTracing(event.timeStamp);
			},
			[ufoPressTracing],
		);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const filterIssue = useCallback(
			(issue: { id?: string; key: string }) =>
				Boolean(issue.id && !isOptimisticIssue(Number(issue.id)) && issue.key),
			[],
		);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ createOpenIssueModalHandler: openIssueModal } = useIssueModalActions({
			onBeforeOpened,
			filterIssue,
		}));
	}

	const { toggleHighlightRow, getRowClassName } = useHighlightDoneItems();

	const highlightRow = useCallback(
		(issueKey: string) => toggleHighlightRow(issueKey, true),
		[toggleHighlightRow],
	);

	const { onIssueUpdated } = useIssueUpdated();

	const columns = useGetColumns({
		columnWidths,
		rowHeight: ROW_HEIGHT,
		openIssueModal,
		highlightRow,
		loading,
		projectId,
		onIssueUpdated,
	});

	const onResize = (columnKey: string, width: number, analyticsEvent: UIAnalyticsEvent) => {
		interactionMetrics[COLUMN_RESIZED].start();
		setColumnWidth(columnKey, width);
		fireUIAnalytics(analyticsEvent, 'column resized', { columnKey });
	};

	const onModalIssueClose = useCallback(() => {
		setSelectedIssueKey(undefined);
	}, [setSelectedIssueKey]);

	const { onIssueChange } = useIssueChangeUpdater();
	// this needs to be memoised otherwise it rerenders when the table changes causing some elements to blink
	// a common reason for the table to change while this is open is the realtime implementation
	const bentoView = useMemo(
		() =>
			selectedIssueKey != null && (
				<ModalIssueApp
					issueKey={toIssueKey(selectedIssueKey)}
					onClose={onModalIssueClose}
					analyticsSource={BUSINESS_LIST}
					onChange={onIssueChange}
					isSoftwareProject={
						fg('jsw_list_view_-_change_issue_view_analytics_event') && isSoftwareFieldsEnabled
					}
				/>
			),
		[selectedIssueKey, onModalIssueClose, onIssueChange, isSoftwareFieldsEnabled],
	);

	const { showFlatList } = useHierarchyConfig();
	const [groupByFieldId] = useGroupBy();

	const isGrouping = groupByFieldId != null;
	const shouldShowEmbedOnboarding =
		selectedIssueKey == null && isOnboardingEnabled && !showFlatList && issuesMap?.size > 3;
	const shouldShowReparentModal = showReparentModal && ff('jwm.visual-hierarchy-dnd');

	useEffect(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'list viewed', {
			isGrouping,
			isSorting,
			isFiltering,
			isSearching,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<UFOLoadHold name="list-view" hold={loading} />
			<RenderStartMark view={featureView} loading={loading} />
			<Hierarchy />
			<BaseTableWithAnalytics
				columns={columns}
				onResize={onResize}
				onEndReachedThreshold={ON_END_REACHED_THRESHOLD}
				footerHeight={FOOTER_HEIGHT}
				rowHeight={ROW_HEIGHT}
				getRowClassName={getRowClassName}
				loadingMessage={formatMessage(messages.loadingMoreItems)}
				isSorting={isSorting}
				EmptyState={EmptyState}
				GroupItem={GroupItem}
				projectId={projectId}
				projectKey={projectKey}
				projectName={projectName}
			/>
			<ListViewUpdater
				{...(ff('jwm.list.background-refetch-for-groups-and-children')
					? {}
					: { issuesMap, isBackgroundQueryDisabled: isGrouping })}
			/>
			<GroupUpdater isBackgroundQueryDisabled={!isGrouping} />
			<UpdateFieldsDetail />
			{shouldShowEmbedOnboarding && <ListEmbedOnboarding />}
			<UFOSegment name="jira-list-issue-app">
				{isViewModeSideBar ? (
					<SidebarIssueView
						issueKey={selectedIssueKey}
						onClose={onModalIssueClose}
						analyticsSource={BUSINESS_LIST}
						onChange={onIssueChange}
						isSoftwareProject={
							fg('jsw_list_view_-_change_issue_view_analytics_event') && isSoftwareFieldsEnabled
						}
					/>
				) : (
					bentoView
				)}
			</UFOSegment>

			{fg('jsw_list_view_-_all_the_fields') && (
				<DevDetailsDialogLoaderAsync scopeId="software-list.list" />
			)}
			<ListActionsBanner />

			<BulkDeleteModal />
			{shouldShowReparentModal && <AsyncReparentModal />}
			<BulkEdit />
		</>
	);
};

export const ListView = ({ projectId, projectKey, projectName, isTeamManaged }: ListViewProps) => {
	const { formatMessage } = useIntl();
	const [{ childrenItems: _childrenQueryData }] = useHierarchyStore();
	const pageHeading = formatMessage(messages.listHeading);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClear = useCallback(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'groupBy cleared', {
			reason: 'stale',
		});
	}, [createAnalyticsEvent]);

	useClearStaleGroupBy({ onClear });

	useWatchBulkSelection();

	useEffect(() => {
		// fire tracking for inline field create statsig experiment
		isJwmInlineFieldCreateEnabled();
	}, []);

	const listViewContent = (
		<>
			<BusinessDocumentTitle pageHeading={pageHeading} />
			<UFOSegment name="jira-list-header-actions">
				<HeaderActions />
			</UFOSegment>
			<ListContent
				projectKey={projectKey}
				projectName={projectName}
				projectId={projectId}
				isTeamManaged={isTeamManaged}
			/>
			<InteractionMetrics />
			{ff('jwm.preload-issue-view-critical-data') && <PrefetchIssueDataController />}
		</>
	);

	return ff('bulk-edit-in-jwm-list-view_bny2w') ? (
		<BulkSelectionContainer scope="JWMListBulkSelection">{listViewContent}</BulkSelectionContainer>
	) : (
		listViewContent
	);
};

export default ListView;
